import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuditLog, useAuditLogFilters } from '../hooks/use-audit-log';
import type {
  AuditAction,
  AuditActorType,
  AuditLogFilterState,
  AuditResourceType,
} from '../types/audit-log.types';
import { getAuditErrorMessage, isForbiddenError } from '../utils/audit-log-utils';
import { AuditLogFilterBar } from '../components/audit-log-filter-bar';
import { AuditLogTable } from '../components/audit-log-table';
import { AuditLogPagination } from '../components/audit-log-pagination';

export const AuditLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL
  const filters: AuditLogFilterState = useMemo(() => {
    const rawActorType = searchParams.get('actorType') as AuditActorType | null;
    const actorId = searchParams.get('actorId') || undefined;
    const action = (searchParams.get('action') as AuditAction) || undefined;
    const resourceType =
      (searchParams.get('resourceType') as AuditResourceType) || undefined;
    const resourceId = searchParams.get('resourceId') || undefined;
    const from = searchParams.get('from') || undefined;
    const to = searchParams.get('to') || undefined;

    const result: AuditLogFilterState = {};
    if (rawActorType) result.actorType = rawActorType;
    if (actorId) result.actorId = actorId;
    if (action) result.action = action;
    if (resourceType) result.resourceType = resourceType;
    if (resourceId) result.resourceId = resourceId;
    if (from) result.from = from;
    if (to) result.to = to;
    return result;
  }, [searchParams]);

  // Derive day strings for the date input fields (YYYY-MM-DD in local time)
  const fromDay = useMemo(() => {
    if (!filters.from) return '';
    try {
      const d = new Date(filters.from);
      return d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    } catch {
      return '';
    }
  }, [filters.from]);

  const toDay = useMemo(() => {
    if (!filters.to) return '';
    try {
      const d = new Date(filters.to);
      return d.toLocaleDateString('en-CA'); // YYYY-MM-DD
    } catch {
      return '';
    }
  }, [filters.to]);

  // Pagination state
  // cursorStack[0] is always undefined (first page). Each subsequent push is the nextCursor
  // returned by the API. pageIndex is the current position in the stack.
  const [pageSize, setPageSize] = useState(50);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  // Reset to page 1 whenever filters or pageSize change
  useEffect(() => {
    setCursorStack([undefined]);
    setPageIndex(0);
  }, [filters, pageSize]);

  const currentCursor = cursorStack[pageIndex];

  const {
    data: filterOptions,
    isLoading: isFiltersLoading,
    error: filtersError,
    refetch: refetchFilters,
  } = useAuditLogFilters();

  const {
    data,
    isLoading: isLogLoading,
    isFetching: isLogFetching,
    error: logError,
    refetch: refetchLog,
  } = useAuditLog(filters, pageSize, currentCursor);

  const entries = data?.items ?? [];
  const pageInfo = data?.pageInfo;

  const isForbidden = isForbiddenError(logError) || isForbiddenError(filtersError);
  const activeError = logError || filtersError;
  const isError = Boolean(activeError && !isForbidden);
  const errorMessage = activeError ? getAuditErrorMessage(activeError) : undefined;

  const hasFilters = Boolean(
    filters.actorType ||
      filters.actorId ||
      filters.action ||
      filters.resourceType ||
      filters.resourceId ||
      filters.from ||
      filters.to
  );

  const handleFilterChange = (nextFilters: AuditLogFilterState) => {
    const nextParams = new URLSearchParams();
    if (nextFilters.actorType) nextParams.set('actorType', nextFilters.actorType);
    if (nextFilters.actorId) nextParams.set('actorId', nextFilters.actorId);
    if (nextFilters.action) nextParams.set('action', nextFilters.action);
    if (nextFilters.resourceType)
      nextParams.set('resourceType', nextFilters.resourceType);
    if (nextFilters.resourceId)
      nextParams.set('resourceId', nextFilters.resourceId.trim());
    if (nextFilters.from) nextParams.set('from', nextFilters.from);
    if (nextFilters.to) nextParams.set('to', nextFilters.to);

    setSearchParams(nextParams, { replace: true });
  };

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const handleRetry = () => {
    refetchFilters();
    refetchLog();
  };

  const handleNextPage = () => {
    if (!pageInfo?.hasNext || !pageInfo.nextCursor) return;
    const nextCursor = pageInfo.nextCursor;
    const nextIndex = pageIndex + 1;

    setCursorStack((prev) => {
      // If we already have the cursor at nextIndex (navigated forward before), keep it.
      if (prev[nextIndex] !== undefined) return prev;
      const updated = [...prev];
      updated[nextIndex] = nextCursor;
      return updated;
    });
    setPageIndex(nextIndex);
  };

  const handlePreviousPage = () => {
    if (pageIndex <= 0) return;
    setPageIndex((prev) => prev - 1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    // useEffect above will reset cursor state when pageSize changes
  };

  const pagination = (
    <AuditLogPagination
      pageSize={pageSize}
      count={entries.length}
      pageIndex={pageIndex}
      hasNextPage={Boolean(pageInfo?.hasNext)}
      hasPreviousPage={pageIndex > 0}
      isFetching={isLogFetching}
      isError={isError}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      onPageSizeChange={handlePageSizeChange}
    />
  );

  return (
    <div className="flex flex-col gap-5 flex-1 min-h-0">
      {/* Page Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div>
          <h1
            data-page-title
            data-testid="audit-log-title"
            className="text-h1 font-semibold text-neutral-900"
          >
            Audit log
          </h1>
          <p className="text-neutral-600 text-sm mt-0.5">
            Every write this organisation has made, newest first. Read-only.
          </p>
        </div>
      </div>

      {/* Hide filters bar on 403 forbidden */}
      {!isForbidden && (
        <AuditLogFilterBar
          filters={filters}
          options={filterOptions}
          fromDay={fromDay}
          toDay={toDay}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
          isLoading={isFiltersLoading}
        />
      )}

      {/* Table Card */}
      <AuditLogTable
        entries={entries}
        isLoading={isLogLoading}
        isError={isError}
        isForbidden={isForbidden}
        errorMessage={errorMessage}
        hasFilters={hasFilters}
        onClearFilters={handleClearFilters}
        onRetry={handleRetry}
        footer={!isForbidden ? pagination : undefined}
      />
    </div>
  );
};
